import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AnalyticsService from '../../services/analyticsService';

// ── Metric Card ──────────────────────────────────────────────────────────────
const MetricCard = ({ label, value, subtext, icon, iconBg, iconColor, trend = undefined, highlight = false }) => (
  <div className={`bg-card border rounded-xl p-5 flex flex-col gap-3 transition-all duration-300 hover:shadow-premium-lg hover:-translate-y-0.5 ${highlight ? 'border-primary/40' : 'border-border'}`}>
    <div className="flex items-start justify-between">
      <div className={`w-11 h-11 ${iconBg} rounded-lg flex items-center justify-center`}>
        <Icon name={icon} size={22} className={iconColor} />
      </div>
      {trend !== undefined && (
        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${trend >= 0 ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
          {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)?.toFixed(2)}%
        </span>
      )}
    </div>
    <div>
      <div className="text-2xl font-bold text-card-foreground tracking-tight">{value}</div>
      <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
      {subtext && <div className="text-xs text-muted-foreground mt-1 opacity-70">{subtext}</div>}
    </div>
  </div>
);

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-premium-lg text-sm">
      <p className="font-medium text-popover-foreground mb-2">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry?.color }} />
          <span className="text-muted-foreground">{entry?.name}:</span>
          <span className="text-popover-foreground font-medium">
            {entry?.name === 'ROI %' ? `${entry?.value?.toFixed(2)}%` : `$${entry?.value?.toLocaleString()}`}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Performer Row ─────────────────────────────────────────────────────────────
const PerformerRow = ({ item, rank, isWorst }) => {
  const roi = item?.roi ?? 0;
  const gain = item?.gain ?? 0;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/20 transition-colors duration-200">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${
        rank === 1 ? 'bg-warning/20 text-warning' :
        rank === 2 ? 'bg-muted/40 text-muted-foreground' :
        rank === 3 ? 'bg-conversion-accent/20 text-conversion-accent': 'bg-muted/20 text-muted-foreground'
      }`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-card-foreground truncate">{item?.name}</p>
        <p className="text-xs text-muted-foreground capitalize">{item?.collectible_type?.replace(/-/g, ' ')}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-semibold text-card-foreground">${parseFloat(item?.current_value || 0)?.toLocaleString()}</div>
        <div className={`text-xs font-medium flex items-center justify-end gap-0.5 ${isWorst ? 'text-destructive' : 'text-success'}`}>
          <Icon name={isWorst ? 'ArrowDown' : 'ArrowUp'} size={11} />
          {Math.abs(roi)?.toFixed(2)}%
          <span className="text-muted-foreground ml-1">({gain >= 0 ? '+' : ''}${gain?.toFixed(0)})</span>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const PortfolioPerformance = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('1Y');
  const [roiChartMode, setRoiChartMode] = useState('roi'); // 'roi' | 'value'
  const [exporting, setExporting] = useState(false);

  const [data, setData] = useState({
    analytics: null,
    trends: [],
    performers: []
  });

  useEffect(() => {
    if (user && !authLoading) loadData();
    else if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, timeRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsRes, trendsRes, performersRes] = await Promise.all([
        AnalyticsService?.getPortfolioAnalytics(user?.id),
        AnalyticsService?.getPortfolioTrends(user?.id, timeRange),
        AnalyticsService?.getTopPerformers(user?.id, 20)
      ]);

      if (analyticsRes?.error) throw analyticsRes?.error;

      setData({
        analytics: analyticsRes?.data,
        trends: trendsRes?.data || [],
        performers: performersRes?.data || []
      });
    } catch (err) {
      setError(err?.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await AnalyticsService?.exportPortfolioCSV(user?.id);
      if (result?.error) throw result?.error;
      const blob = new Blob([result?.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `portfolio_performance_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (err) {
      setError(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ── Derived metrics ──────────────────────────────────────────────────────
  const analytics = data?.analytics;
  const totalGainLoss = analytics?.unrealizedGains ?? 0;
  const portfolioROI = analytics?.portfolioROI ?? 0;
  const totalValue = analytics?.totalValue ?? 0;
  const totalCost = analytics?.totalCost ?? 0;
  const totalItems = analytics?.totalItems ?? 0;

  // Annualized return: approximate from ROI and time range
  const timeRangeDays = { '1M': 30, '3M': 90, '6M': 180, '1Y': 365, 'All': 730 };
  const days = timeRangeDays?.[timeRange] || 365;
  const annualizedReturn = totalCost > 0
    ? (Math.pow(1 + portfolioROI / 100, 365 / days) - 1) * 100
    : 0;

  // Best / worst performers (sorted by ROI)
  const sortedPerformers = [...(data?.performers || [])]?.sort((a, b) => (b?.roi ?? 0) - (a?.roi ?? 0));
  const bestPerformers = sortedPerformers?.slice(0, 5);
  const worstPerformers = [...sortedPerformers]?.reverse()?.slice(0, 5);

  // Chart data: format trend dates
  const chartData = (data?.trends || [])?.map(t => ({
    ...t,
    label: typeof t?.date === 'string' ? new Date(t.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : t?.date
  }));

  const timeRangeOptions = ['1M', '3M', '6M', '1Y', 'All'];

  // ── Loading ──────────────────────────────────────────────────────────────
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="text-muted-foreground text-sm">Loading performance data…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 lg:p-8 max-w-md mx-auto text-center mt-20">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Performance Data</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadData}>Try Again</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-4 sm:p-6 lg:p-8">

          {/* ── Page Header ── */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="BarChart3" size={18} className="text-primary" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Portfolio Performance</h1>
                </div>
                <p className="text-muted-foreground text-sm sm:text-base ml-10">
                  Total gain/loss, annualized returns, and ROI trends across your collection.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {/* Time Range */}
                <div className="flex bg-muted/30 rounded-lg p-1">
                  {timeRangeOptions?.map(opt => (
                    <button
                      key={opt}
                      onClick={() => setTimeRange(opt)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 min-h-[36px] ${
                        timeRange === opt
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-card-foreground'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <Button onClick={handleExport} loading={exporting} iconName="Download" variant="outline" size="md">
                    Export
                  </Button>
                  <Button onClick={loadData} iconName="RefreshCw" variant="default" size="md">
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* ── KPI Metric Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            <MetricCard
              label="Total Gain / Loss"
              value={`${totalGainLoss >= 0 ? '+' : ''}$${Math.abs(totalGainLoss)?.toLocaleString()}`}
              subtext={`Cost basis: $${totalCost?.toLocaleString()}`}
              icon={totalGainLoss >= 0 ? 'TrendingUp' : 'TrendingDown'}
              iconBg={totalGainLoss >= 0 ? 'bg-success/10' : 'bg-destructive/10'}
              iconColor={totalGainLoss >= 0 ? 'text-success' : 'text-destructive'}
              trend={portfolioROI}
              highlight
            />
            <MetricCard
              label="Portfolio ROI"
              value={`${portfolioROI >= 0 ? '+' : ''}${portfolioROI?.toFixed(2)}%`}
              subtext="Return on investment"
              icon="Percent"
              iconBg="bg-primary/10"
              iconColor="text-primary"
              trend={portfolioROI}
            />
            <MetricCard
              label="Annualized Return"
              value={`${annualizedReturn >= 0 ? '+' : ''}${annualizedReturn?.toFixed(2)}%`}
              subtext={`Based on ${timeRange} window`}
              icon="CalendarDays"
              iconBg="bg-warning/10"
              iconColor="text-warning"
            />
            <MetricCard
              label="Total Portfolio Value"
              value={`$${totalValue?.toLocaleString()}`}
              subtext={`${totalItems?.toLocaleString()} item${totalItems !== 1 ? 's' : ''}`}
              icon="Wallet"
              iconBg="bg-conversion-accent/10"
              iconColor="text-conversion-accent"
            />
          </div>

          {/* ── ROI Trends Chart ── */}
          <div className="bg-card border border-border rounded-xl p-5 sm:p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div>
                <h2 className="text-lg font-semibold text-card-foreground">ROI Trends Over Time</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {roiChartMode === 'roi' ? 'Return on investment (%)' : 'Portfolio value ($)'} — {timeRange} view
                </p>
              </div>
              <div className="flex bg-muted/30 rounded-lg p-1 self-start sm:self-auto">
                <button
                  onClick={() => setRoiChartMode('roi')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    roiChartMode === 'roi' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-card-foreground'
                  }`}
                >
                  <Icon name="Percent" size={14} />
                  ROI %
                </button>
                <button
                  onClick={() => setRoiChartMode('value')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    roiChartMode === 'value' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-card-foreground'
                  }`}
                >
                  <Icon name="DollarSign" size={14} />
                  Value
                </button>
              </div>
            </div>

            {chartData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={portfolioROI >= 0 ? '#10B981' : '#EF4444'} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={portfolioROI >= 0 ? '#10B981' : '#EF4444'} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="label"
                    stroke="#A0A3B1"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    stroke="#A0A3B1"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v =>
                      roiChartMode === 'roi'
                        ? `${v?.toFixed(1)}%`
                        : `$${(v / 1000)?.toFixed(0)}k`
                    }
                    width={55}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  {roiChartMode === 'roi' && (
                    <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" strokeDasharray="4 4" />
                  )}
                  <Area
                    type="monotone"
                    dataKey={roiChartMode === 'roi' ? 'roi' : 'value'}
                    name={roiChartMode === 'roi' ? 'ROI %' : 'Portfolio Value'}
                    stroke={roiChartMode === 'roi'
                      ? (portfolioROI >= 0 ? '#10B981' : '#EF4444')
                      : 'var(--color-primary)'}
                    strokeWidth={2.5}
                    fill={roiChartMode === 'roi' ? 'url(#roiGradient)' : 'url(#valueGradient)'}
                    dot={false}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex flex-col items-center justify-center text-center">
                <Icon name="LineChart" size={40} className="text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">No trend data available yet.</p>
                <p className="text-xs text-muted-foreground mt-1">Add items with purchase prices to track ROI over time.</p>
              </div>
            )}
          </div>

          {/* ── Best & Worst Performers ── */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
            {/* Best Performers */}
            <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={18} className="text-success" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-card-foreground">Best Performers</h2>
                  <p className="text-xs text-muted-foreground">Highest ROI items in your portfolio</p>
                </div>
              </div>

              {bestPerformers?.length > 0 ? (
                <div className="space-y-1">
                  {bestPerformers?.map((item, i) => (
                    <PerformerRow key={item?.id || i} item={item} rank={i + 1} isWorst={false} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <Icon name="Award" size={36} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No performers yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Add items with purchase prices to see rankings.</p>
                </div>
              )}

              {bestPerformers?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-success">
                      +{(bestPerformers?.[0]?.roi ?? 0)?.toFixed(2)}%
                    </div>
                    <div className="text-xs text-muted-foreground">Top ROI</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-primary">
                      {bestPerformers?.length > 0
                        ? `+${(bestPerformers?.reduce((s, p) => s + (p?.roi ?? 0), 0) / bestPerformers?.length)?.toFixed(2)}%`
                        : '—'}
                    </div>
                    <div className="text-xs text-muted-foreground">Avg ROI (top 5)</div>
                  </div>
                </div>
              )}
            </div>

            {/* Worst Performers */}
            <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 bg-destructive/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingDown" size={18} className="text-destructive" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-card-foreground">Worst Performers</h2>
                  <p className="text-xs text-muted-foreground">Items with lowest ROI — review or revalue</p>
                </div>
              </div>

              {worstPerformers?.length > 0 ? (
                <div className="space-y-1">
                  {worstPerformers?.map((item, i) => (
                    <PerformerRow key={item?.id || i} item={item} rank={i + 1} isWorst={(item?.roi ?? 0) < 0} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <Icon name="ShieldCheck" size={36} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No underperformers found.</p>
                  <p className="text-xs text-muted-foreground mt-1">All items are performing well or have no price data.</p>
                </div>
              )}

              {worstPerformers?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className={`text-lg font-bold ${(worstPerformers?.[worstPerformers?.length - 1]?.roi ?? 0) < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {(worstPerformers?.[worstPerformers?.length - 1]?.roi ?? 0)?.toFixed(2)}%
                    </div>
                    <div className="text-xs text-muted-foreground">Lowest ROI</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-muted-foreground">
                      {worstPerformers?.length > 0
                        ? `${(worstPerformers?.reduce((s, p) => s + (p?.roi ?? 0), 0) / worstPerformers?.length)?.toFixed(2)}%`
                        : '—'}
                    </div>
                    <div className="text-xs text-muted-foreground">Avg ROI (bottom 5)</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Gain/Loss Bar Chart by Collection ── */}
          {analytics?.collections?.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5 sm:p-6 mb-8">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="BarChart2" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-card-foreground">Gain / Loss by Collection</h2>
                  <p className="text-xs text-muted-foreground">Unrealized gains and losses per collection</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={analytics?.collections?.map(c => ({
                    name: c?.name?.length > 14 ? c?.name?.slice(0, 14) + '…' : c?.name,
                    gain: parseFloat(((c?.value ?? 0) - (c?.cost ?? 0))?.toFixed(2)),
                    roi: parseFloat((c?.roi ?? 0)?.toFixed(2))
                  }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="#A0A3B1" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#A0A3B1" fontSize={11} tickLine={false} axisLine={false}
                    tickFormatter={v => `$${v >= 0 ? '' : '-'}${Math.abs(v / 1000)?.toFixed(0)}k`}
                    width={50}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
                  <Bar
                    dataKey="gain"
                    name="Gain/Loss"
                    radius={[4, 4, 0, 0]}
                    fill="#10B981"
                    label={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── Quick Actions ── */}
          <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
            <h3 className="text-base font-semibold text-card-foreground mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/advanced-dashboard')} iconName="LineChart" variant="outline" size="md">
                Advanced Analytics
              </Button>
              <Button onClick={() => navigate('/collection-dashboard')} iconName="LayoutDashboard" variant="outline" size="md">
                Collection Dashboard
              </Button>
              <Button onClick={() => navigate('/market-analysis')} iconName="TrendingUp" variant="outline" size="md">
                Market Analysis
              </Button>
              <Button onClick={handleExport} loading={exporting} iconName="Download" variant="outline" size="md">
                Export CSV
              </Button>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default PortfolioPerformance;
